import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { fetchAuthSession } from '@aws-amplify/auth';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import Chart from '../components/Chart';
import useTranslation from '../services/translationService';
import { fetchAdminGameAnalytics } from '../services/adminAnalyticsService';
import { getGameNameWithFallback } from '../utils/gameNameMapping';
import {
  BOOK_LABELS,
  bookLabel,
  isDatabetrixGameId,
  isDatabetrixProvider,
  providerLabel
} from '../utils/gameProviders';
import { USD_RATES, toUsd } from '../utils/currency';

const TZ = 'Asia/Beirut';
const CURRENCIES = ['USD', 'LBP', 'TND', 'TRY', 'IQD', 'KWD'];
const PAGE_SIZE = 20;
const OVERVIEW_ROWS = 5;

const pad2 = (n) => String(n).padStart(2, '0');

const beirutWall = (year, month, day, hour = 0, minute = 0, second = 0, ms = 0) => {
  const stamp = `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}:${pad2(second)}.${String(ms).padStart(3, '0')}`;
  return fromZonedTime(stamp, TZ);
};

const beirutParts = (date = new Date()) => {
  const zoned = formatInTimeZone(date, TZ, 'yyyy-MM-dd-HH-mm');
  const [year, month, day, hour, minute] = zoned.split('-').map(Number);
  return { year, month, day, hour, minute };
};

const rangeForPreset = (preset, customStart, customEnd) => {
  const now = new Date();
  const { year, month, day } = beirutParts(now);
  const todayStart = beirutWall(year, month, day, 0, 0, 0, 0);
  const todayEnd = beirutWall(year, month, day, 23, 59, 59, 999);

  if (preset === 'today') return { start: todayStart, end: todayEnd };
  if (preset === 'last7') {
    const startDay = new Date(Date.UTC(year, month - 1, day - 6));
    return {
      start: beirutWall(startDay.getUTCFullYear(), startDay.getUTCMonth() + 1, startDay.getUTCDate(), 0, 0, 0, 0),
      end: todayEnd
    };
  }
  if (preset === 'thisMonth') {
    return { start: beirutWall(year, month, 1, 0, 0, 0, 0), end: todayEnd };
  }
  if (preset === 'lastMonth') {
    let prevYear = year;
    let prevMonth = month - 1;
    if (prevMonth < 1) {
      prevMonth = 12;
      prevYear -= 1;
    }
    return {
      start: beirutWall(prevYear, prevMonth, 1, 0, 0, 0, 0),
      end: new Date(beirutWall(year, month, 1, 0, 0, 0, 0).getTime() - 1)
    };
  }
  return { start: customStart || todayStart, end: customEnd || todayEnd };
};

// Phone-sized cards cannot fit raw LBP billions, so money is abbreviated in the
// UI and the untruncated value stays available as a title attribute.
const compact = (value) => {
  const n = Number.isFinite(value) ? value : 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${abs.toFixed(abs < 100 && abs > 0 ? 2 : 0)}`;
};

const fullNumber = (value, currency) => {
  const n = Number.isFinite(value) ? value : 0;
  const digits = currency === 'LBP' || currency === 'IQD' ? 0 : currency === 'KWD' ? 3 : 2;
  return n.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
};

const money = (value, unit) => {
  const n = Number.isFinite(value) ? value : 0;
  if (unit === 'USD') return `${n < 0 ? '-' : ''}$${compact(Math.abs(n))}`;
  return `${compact(n)} ${unit}`;
};
const moneyTitle = (value, unit) => `${fullNumber(value, unit)} ${unit}`;
const count = (value) => (Number(value) || 0).toLocaleString('en-US');
const holdPct = (ggr, turnover) => (turnover > 0 ? `${((ggr / turnover) * 100).toFixed(1)}%` : '—');
const sharePct = (value, total) => {
  if (!(total > 0)) return null;
  const share = (value / total) * 100;
  return `${share.toFixed(Math.abs(share) < 10 ? 1 : 0)}%`;
};

const toDateTimeLocal = (date) => (date ? formatInTimeZone(date, TZ, "yyyy-MM-dd'T'HH:mm") : '');

const RATES_NOTE = CURRENCIES
  .filter((code) => code !== 'USD')
  .map((code) => `${USD_RATES[code].toLocaleString('en-US')} ${code}`)
  .join(' · ');

const ggrTone = (value) => (value < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400');

/**
 * Folds the per-currency rows the API returns into one row per key. With every
 * currency selected the amounts become USD equivalents so providers and games
 * can be ranked against each other; the native rows stay in `parts` for the
 * detail view.
 */
const mergeRows = (rows, keyOf, convert) => {
  const merged = new Map();
  rows.forEach((row) => {
    const key = keyOf(row);
    const toUnit = (value) => (convert ? toUsd(value, row.currency) : Number(value) || 0);
    const entry = merged.get(key) || {
      id: key, ggr: 0, turnover: 0, paidOut: 0, bets: 0, players: 0, selections: 0, parts: []
    };
    entry.ggr += toUnit(row.ggr);
    entry.turnover += toUnit(row.betAmount);
    entry.paidOut += toUnit(row.winAmount);
    entry.bets += Number(row.betCount) || 0;
    entry.players += Number(row.uniquePlayers) || 0;
    entry.selections += Number(row.selections) || 0;
    entry.parts.push(row);
    merged.set(key, entry);
  });
  return [...merged.values()];
};

const totalsOf = (rows) => rows.reduce((acc, row) => ({
  ggr: acc.ggr + row.ggr,
  turnover: acc.turnover + row.turnover,
  bets: acc.bets + row.bets,
  players: acc.players + row.players
}), { ggr: 0, turnover: 0, bets: 0, players: 0 });

const RankBadge = ({ index }) => {
  const styles = [
    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
  ];
  const style = styles[index] || 'bg-gray-100 text-gray-500 dark:bg-gray-700/50 dark:text-gray-400';
  return (
    <span className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-lg text-[11px] font-bold ${style}`}>
      {index + 1}
    </span>
  );
};

const Pill = ({ children, tone = 'gray' }) => {
  const tones = {
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300',
    rose: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
  };
  return (
    <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
};

const Stat = ({ label, value, title, className = '' }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</div>
    <div title={title} className={`text-xs sm:text-sm font-semibold truncate tabular-nums text-gray-900 dark:text-gray-100 ${className}`}>
      {value}
    </div>
  </div>
);

const Segmented = ({ options, value, onChange, label }) => (
  <div role="radiogroup" aria-label={label} className="inline-flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800/80">
    {options.map((option) => (
      <button
        key={option.id}
        type="button"
        role="radio"
        aria-checked={value === option.id}
        onClick={() => onChange(option.id)}
        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
          value === option.id
            ? 'bg-white dark:bg-gray-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
            : 'text-gray-600 dark:text-gray-400'
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const SectionHeader = ({ title, hint, action }) => (
  <div className="flex items-end justify-between gap-3 px-1">
    <div className="min-w-0">
      <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">{title}</h2>
      {hint ? <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{hint}</p> : null}
    </div>
    {action}
  </div>
);

const LinkButton = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="shrink-0 inline-flex items-center gap-0.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 active:opacity-70"
  >
    {children}
    <ChevronRightIcon className="w-3.5 h-3.5" />
  </button>
);

const EmptyState = ({ children }) => (
  <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 px-4 py-10 text-center
    text-sm text-gray-500 dark:text-gray-400">
    {children}
  </div>
);

const BAR_TONES = {
  pos: 'bg-emerald-500 dark:bg-emerald-400',
  neg: 'bg-rose-500 dark:bg-rose-400',
  neutral: 'bg-indigo-500 dark:bg-indigo-400'
};

const VALUE_TONES = {
  pos: 'text-emerald-600 dark:text-emerald-400',
  neg: 'text-rose-600 dark:text-rose-400',
  neutral: 'text-gray-900 dark:text-gray-100'
};

/**
 * One ranked entry: what it is, how big it is next to the leader and its share
 * of the total. Everything else sits behind the tap so a phone shows the
 * ranking first.
 */
const RankRow = ({ index, title, badge, subtitle, value, valueTitle, tone, bar, share, open, onToggle, details }) => (
  <li className="border-t first:border-t-0 border-gray-100 dark:border-gray-700/60">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 text-left
        hover:bg-gray-50 dark:hover:bg-gray-700/30 active:bg-gray-50 dark:active:bg-gray-700/40"
    >
      <RankBadge index={index} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{title}</span>
          {badge}
        </div>
        <div className="mt-1 flex items-center gap-2">
          <div className="h-1.5 flex-1 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <div className={`h-full rounded-full ${BAR_TONES[tone]}`} style={{ width: `${Math.max(2, Math.min(100, bar))}%` }} />
          </div>
          {share ? <span className="w-10 text-right text-[10px] tabular-nums text-gray-500 dark:text-gray-400">{share}</span> : null}
        </div>
        {subtitle ? <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 truncate">{subtitle}</div> : null}
      </div>
      <div title={valueTitle} className={`shrink-0 text-sm font-bold tabular-nums ${VALUE_TONES[tone]}`}>{value}</div>
      <ChevronDownIcon className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open ? <div className="px-3 sm:px-4 pb-3 space-y-3">{details()}</div> : null}
  </li>
);

const RankList = ({ children }) => (
  <ul className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
    {children}
  </ul>
);

const DetailStats = ({ children }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2 rounded-xl bg-gray-50 dark:bg-gray-900/40 p-2.5">
    {children}
  </div>
);

const Disclosure = ({ title, hint, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-3 sm:px-4 py-3 text-left"
      >
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</div>
          {hint ? <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{hint}</div> : null}
        </div>
        <ChevronDownIcon className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? <div className="px-3 sm:px-4 pb-3 sm:pb-4">{children}</div> : null}
    </section>
  );
};

const SummaryCard = ({ label, value, valueTitle, tone, parts, stats, note }) => (
  <div className="p-4 sm:p-5 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
    <div className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
    <div title={valueTitle} className={`mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight tabular-nums ${tone}`}>{value}</div>
    {parts?.length ? (
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        {parts.map((part) => (
          <span key={part.label}>
            {part.label}
            {part.value ? (
              <>
                {' '}
                <span title={part.title} className={`font-semibold tabular-nums ${part.tone || 'text-gray-700 dark:text-gray-300'}`}>
                  {part.value}
                </span>
              </>
            ) : null}
          </span>
        ))}
      </div>
    ) : null}
    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 grid grid-cols-3 gap-3">
      {stats.map((stat) => <Stat key={stat.label} {...stat} />)}
    </div>
    {note ? <p className="mt-3 text-[10px] leading-snug text-gray-400 dark:text-gray-500">{note}</p> : null}
  </div>
);

const LoadMore = ({ remaining, onClick, label }) => (remaining > 0 ? (
  <button
    type="button"
    onClick={onClick}
    className="w-full py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-sm font-medium
      text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 active:bg-gray-50 dark:active:bg-gray-700"
  >
    {label} ({count(remaining)})
  </button>
) : null);

const groupedOptions = {
  plugins: { legend: { display: true, position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
  scales: {
    x: { ticks: { font: { size: 10 } } },
    y: { beginAtZero: true, ticks: { callback: (v) => compact(v), font: { size: 10 } } }
  }
};

const AdminAnalytics = () => {
  const { t } = useTranslation();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [preset, setPreset] = useState('today');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [currency, setCurrency] = useState('all');
  const [tab, setTab] = useState('overview');
  const [metric, setMetric] = useState('ggr');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [openRow, setOpenRow] = useState(null);
  const [gameLimit, setGameLimit] = useState(PAGE_SIZE);
  const [providerLimit, setProviderLimit] = useState(PAGE_SIZE);
  const [leagueLimit, setLeagueLimit] = useState(PAGE_SIZE);
  const [leagueView, setLeagueView] = useState('winning');
  const tabsRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const groups = tokens?.accessToken?.payload['cognito:groups'] || [];
        if (!cancelled) setIsAdmin(groups.includes('Admins'));
      } catch (err) {
        console.error('[AdminAnalytics] auth check failed', err);
        if (!cancelled) setIsAdmin(false);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dateRange = useMemo(
    () => rangeForPreset(preset, customStart, customEnd),
    [preset, customStart, customEnd]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAdminGameAnalytics({
        startDate: dateRange.start,
        endDate: dateRange.end,
        currency: currency === 'all' ? null : currency,
        tab: 'overview'
      });
      setData(result);
    } catch (err) {
      console.error('[AdminAnalytics] load failed', err);
      setError(err?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [dateRange.start, dateRange.end, currency]);

  useEffect(() => {
    if (authChecked && isAdmin) load();
  }, [authChecked, isAdmin, load]);

  useEffect(() => {
    setGameLimit(PAGE_SIZE);
    setProviderLimit(PAGE_SIZE);
    setLeagueLimit(PAGE_SIZE);
    setOpenRow(null);
  }, [search, currency, preset, tab, leagueView, metric]);

  // Follows the response rather than the filter, so rows from the previous
  // currency are never shown in the new one's unit while it loads.
  const loadedCurrencies = data?.currencies || CURRENCIES;
  const converted = loadedCurrencies.length > 1;
  const unit = converted ? 'USD' : loadedCurrencies[0];
  const query = tab === 'casino' ? search.trim().toLowerCase() : '';

  const matches = useCallback((...fields) => (
    !query || fields.some((field) => String(field || '').toLowerCase().includes(query))
  ), [query]);

  const metricOf = useCallback((row) => (metric === 'turnover' ? row.turnover : row.ggr), [metric]);

  const providers = useMemo(() => mergeRows(data?.casino?.providers || [], (row) => row.provider || 'unknown', converted)
    .map((row) => ({ ...row, provider: row.id, name: providerLabel(row.id) })), [data, converted]);

  const games = useMemo(() => mergeRows(data?.casino?.games || [], (row) => row.gameId, converted)
    .map((row) => ({
      ...row,
      gameId: row.id,
      provider: row.parts[0]?.provider || 'unknown',
      name: getGameNameWithFallback(row.id, row.id)
    })), [data, converted]);

  const books = useMemo(() => mergeRows(
    (data?.sports?.books || []).filter((row) => BOOK_LABELS[row.book]),
    (row) => row.book,
    converted
  ).map((row) => ({ ...row, book: row.id, name: bookLabel(row.id) })), [data, converted]);

  const leagues = useMemo(() => mergeRows(
    data?.sports?.leagues || [],
    (row) => `${row.sport}|${row.league}`,
    converted
  ).map((row) => ({
    ...row,
    sport: row.parts[0]?.sport || 'Unknown',
    league: row.parts[0]?.league || row.id
  })), [data, converted]);

  const casinoTotals = useMemo(() => totalsOf(providers), [providers]);
  const sportTotals = useMemo(() => totalsOf(books), [books]);

  const rankedProviders = useMemo(() => providers
    .filter((row) => matches(row.name, row.provider))
    .sort((a, b) => metricOf(b) - metricOf(a)), [providers, matches, metricOf]);

  const rankedGames = useMemo(() => games
    .filter((row) => matches(row.name, row.gameId, providerLabel(row.provider)))
    .sort((a, b) => metricOf(b) - metricOf(a)), [games, matches, metricOf]);

  const rankedBooks = useMemo(() => [...books].sort((a, b) => b.ggr - a.ggr), [books]);

  const rankedLeagues = useMemo(() => {
    const dir = leagueView === 'losing' ? -1 : 1;
    return [...leagues].sort((a, b) => dir * (b.ggr - a.ggr));
  }, [leagues, leagueView]);

  const gamesByProvider = useMemo(() => {
    const map = new Map();
    games.forEach((game) => {
      const list = map.get(game.provider) || [];
      list.push(game);
      map.set(game.provider, list);
    });
    return map;
  }, [games]);

  const liveCurrencies = useMemo(() => (data?.currencies || CURRENCIES)
    .map((code) => ({
      code,
      casino: data?.casino?.byCurrency?.[code]?.summary || {},
      sport: data?.sports?.byCurrency?.[code]?.summary || {}
    }))
    .filter(({ casino, sport }) => (casino.betCount || 0) + (sport.betCount || 0) > 0), [data]);

  const playersChart = useMemo(() => {
    const rows = data?.sports?.books || [];
    const labels = liveCurrencies.map((c) => c.code);
    return {
      labels,
      datasets: Object.keys(BOOK_LABELS).map((book, i) => ({
        label: bookLabel(book),
        data: labels.map((code) => rows.find((b) => b.book === book && b.currency === code)?.uniquePlayers || 0),
        backgroundColor: ['rgba(79, 70, 229, 0.85)', 'rgba(16, 185, 129, 0.85)'][i % 2],
        borderRadius: 5,
        maxBarThickness: 26
      }))
    };
  }, [data, liveCurrencies]);

  const toggleRow = useCallback((key) => setOpenRow((current) => (current === key ? null : key)), []);

  const goTo = useCallback((nextTab) => {
    setTab(nextTab);
    tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (authChecked && !isAdmin) return <Navigate to="/dashboard" replace />;

  if (!authChecked) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500 dark:text-gray-400 text-sm">
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  const ingest = data?.ingest;
  const catchingUp = ingest?.status === 'backfill' || ingest?.status === 'pending';
  const ggrLabel = t('adminAnalytics.ggr', 'GGR');
  const turnoverLabel = t('adminAnalytics.turnover', 'Turnover');
  const holdLabel = t('adminAnalytics.hold', 'Hold');
  const betsLabel = t('adminAnalytics.bets', 'Bets');
  const playersLabel = t('adminAnalytics.players', 'Players');
  const unitLabel = converted ? '≈ USD' : unit;
  const convertedNote = converted ? t('adminAnalytics.convertedNote', { rates: RATES_NOTE }) : null;

  const tabs = [
    { id: 'overview', label: t('adminAnalytics.overview', 'Overview') },
    { id: 'casino', label: t('adminAnalytics.casino', 'Casino') },
    { id: 'sport', label: t('adminAnalytics.sport', 'Sports') }
  ];

  const metricOptions = [
    { id: 'ggr', label: ggrLabel },
    { id: 'turnover', label: turnoverLabel }
  ];

  const moneyStat = (label, value, extra = {}) => ({
    label,
    value: money(value, unit),
    title: moneyTitle(value, unit),
    ...extra
  });

  const valueTone = (value) => {
    if (metric === 'turnover') return 'neutral';
    return value < 0 ? 'neg' : 'pos';
  };

  const otherMetric = (row) => (metric === 'turnover'
    ? `${ggrLabel} ${money(row.ggr, unit)}`
    : `${turnoverLabel} ${money(row.turnover, unit)}`);

  // Per-currency rows keep their own currency, so they read in native units.
  const currencyBreakdown = (parts) => (converted && parts.length > 0 ? (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {t('adminAnalytics.perCurrency', 'Per currency')}
      </div>
      <table className="w-full text-xs tabular-nums">
        <thead>
          <tr className="text-[10px] text-gray-400 dark:text-gray-500">
            <th className="py-1 text-left font-medium">{t('adminAnalytics.currency', 'Currency')}</th>
            <th className="py-1 text-right font-medium">{ggrLabel}</th>
            <th className="py-1 text-right font-medium">{turnoverLabel}</th>
            <th className="py-1 text-right font-medium">{holdLabel}</th>
          </tr>
        </thead>
        <tbody>
          {[...parts].sort((a, b) => toUsd(b.ggr, b.currency) - toUsd(a.ggr, a.currency)).map((part) => (
            <tr key={part.currency} className="border-t border-gray-100 dark:border-gray-700/60 align-top">
              <td className="py-1.5 font-semibold text-gray-700 dark:text-gray-200">{part.currency}</td>
              <td className="py-1.5 text-right">
                <span title={moneyTitle(part.ggr, part.currency)} className={ggrTone(part.ggr)}>
                  {compact(part.ggr)}
                </span>
                {part.currency !== 'USD' ? (
                  <span className="block text-[10px] text-gray-400 dark:text-gray-500">
                    ≈ {money(toUsd(part.ggr, part.currency), 'USD')}
                  </span>
                ) : null}
              </td>
              <td className="py-1.5 text-right text-gray-700 dark:text-gray-300" title={moneyTitle(part.betAmount, part.currency)}>
                {compact(part.betAmount)}
              </td>
              <td className="py-1.5 text-right text-gray-700 dark:text-gray-300">{holdPct(part.ggr, part.betAmount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : null);

  const providerRow = (row, index, scale) => {
    const key = `provider:${row.id}`;
    const value = metricOf(row);
    const providerGames = gamesByProvider.get(row.provider) || [];
    return (
      <RankRow
        key={key}
        index={index}
        title={row.name}
        badge={isDatabetrixProvider(row.provider) ? <Pill tone="rose">Databetrix</Pill> : null}
        subtitle={`${otherMetric(row)} · ${holdLabel} ${holdPct(row.ggr, row.turnover)}`}
        value={money(value, unit)}
        valueTitle={moneyTitle(value, unit)}
        tone={valueTone(value)}
        bar={(Math.abs(value) / scale) * 100}
        share={sharePct(value, metric === 'turnover' ? casinoTotals.turnover : casinoTotals.ggr)}
        open={openRow === key}
        onToggle={() => toggleRow(key)}
        details={() => (
          <>
            <DetailStats>
              <Stat {...moneyStat(ggrLabel, row.ggr, { className: ggrTone(row.ggr) })} />
              <Stat {...moneyStat(turnoverLabel, row.turnover)} />
              <Stat label={holdLabel} value={holdPct(row.ggr, row.turnover)} />
              <Stat label={betsLabel} value={count(row.bets)} />
              <Stat label={t('adminAnalytics.playerDays', 'Player-days')} value={count(row.players)} />
              <Stat label={t('adminAnalytics.games', 'Games')} value={count(providerGames.length)} />
            </DetailStats>
            {currencyBreakdown(row.parts)}
            {providerGames.length > 0 ? (
              <div>
                <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {t('adminAnalytics.providerTopGames', 'Top games from this provider')}
                </div>
                <ul className="space-y-1">
                  {[...providerGames].sort((a, b) => metricOf(b) - metricOf(a)).slice(0, 5).map((game) => (
                    <li key={game.id} className="flex items-center justify-between gap-3 text-xs">
                      <span className="truncate text-gray-700 dark:text-gray-300">{game.name}</span>
                      <span
                        title={moneyTitle(metricOf(game), unit)}
                        className={`shrink-0 tabular-nums font-semibold ${VALUE_TONES[valueTone(metricOf(game))]}`}
                      >
                        {money(metricOf(game), unit)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      />
    );
  };

  const gameRow = (row, index, scale) => {
    const key = `game:${row.id}`;
    const value = metricOf(row);
    return (
      <RankRow
        key={key}
        index={index}
        title={row.name}
        badge={isDatabetrixGameId(row.gameId) ? <Pill tone="rose">Databetrix</Pill> : null}
        subtitle={`${providerLabel(row.provider)} · ${otherMetric(row)}`}
        value={money(value, unit)}
        valueTitle={moneyTitle(value, unit)}
        tone={valueTone(value)}
        bar={(Math.abs(value) / scale) * 100}
        share={sharePct(value, metric === 'turnover' ? casinoTotals.turnover : casinoTotals.ggr)}
        open={openRow === key}
        onToggle={() => toggleRow(key)}
        details={() => (
          <>
            <DetailStats>
              <Stat {...moneyStat(ggrLabel, row.ggr, { className: ggrTone(row.ggr) })} />
              <Stat {...moneyStat(turnoverLabel, row.turnover)} />
              <Stat label={holdLabel} value={holdPct(row.ggr, row.turnover)} />
              <Stat label={betsLabel} value={count(row.bets)} />
            </DetailStats>
            {currencyBreakdown(row.parts)}
          </>
        )}
      />
    );
  };

  const bookRow = (row, index, scale) => {
    const key = `book:${row.id}`;
    return (
      <RankRow
        key={key}
        index={index}
        title={row.name}
        subtitle={`${count(row.players)} ${playersLabel.toLowerCase()} · ${count(row.bets)} ${betsLabel.toLowerCase()}`}
        value={money(row.ggr, unit)}
        valueTitle={moneyTitle(row.ggr, unit)}
        tone={row.ggr < 0 ? 'neg' : 'pos'}
        bar={(Math.abs(row.ggr) / scale) * 100}
        share={sharePct(row.ggr, sportTotals.ggr)}
        open={openRow === key}
        onToggle={() => toggleRow(key)}
        details={() => (
          <>
            <DetailStats>
              <Stat {...moneyStat(ggrLabel, row.ggr, { className: ggrTone(row.ggr) })} />
              <Stat {...moneyStat(turnoverLabel, row.turnover)} />
              <Stat label={playersLabel} value={count(row.players)} />
              <Stat label={betsLabel} value={count(row.bets)} />
            </DetailStats>
            {currencyBreakdown(row.parts)}
          </>
        )}
      />
    );
  };

  const leagueRow = (row, index, scale) => {
    const key = `league:${row.id}`;
    return (
      <RankRow
        key={key}
        index={index}
        title={row.league}
        subtitle={`${row.sport} · ${turnoverLabel} ${money(row.turnover, unit)}`}
        value={money(row.ggr, unit)}
        valueTitle={moneyTitle(row.ggr, unit)}
        tone={row.ggr < 0 ? 'neg' : 'pos'}
        bar={(Math.abs(row.ggr) / scale) * 100}
        share={null}
        open={openRow === key}
        onToggle={() => toggleRow(key)}
        details={() => (
          <>
            <DetailStats>
              <Stat {...moneyStat(ggrLabel, row.ggr, { className: ggrTone(row.ggr) })} />
              <Stat {...moneyStat(turnoverLabel, row.turnover)} />
              <Stat {...moneyStat(t('adminAnalytics.paidOut', 'Paid out'), row.paidOut)} />
              <Stat label={t('adminAnalytics.legs', 'Legs')} value={count(row.selections)} />
            </DetailStats>
            {currencyBreakdown(row.parts)}
          </>
        )}
      />
    );
  };

  const rankedSection = (rows, limit, renderRow, empty, scaleOf = metricOf) => {
    if (rows.length === 0) return <EmptyState>{empty}</EmptyState>;
    const scale = rows.reduce((max, row) => Math.max(max, Math.abs(scaleOf(row))), 0) || 1;
    return <RankList>{rows.slice(0, limit).map((row, index) => renderRow(row, index, scale))}</RankList>;
  };

  const ggrOf = (row) => row.ggr;
  const totalGgr = casinoTotals.ggr + sportTotals.ggr;
  const totalTurnover = casinoTotals.turnover + sportTotals.turnover;

  return (
    <div className="space-y-4 sm:space-y-6 pb-24 sm:pb-10">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
            {t('navigation.adminAnalytics', 'Admin Analytics')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {formatInTimeZone(dateRange.start, TZ, 'MMM d')} → {formatInTimeZone(dateRange.end, TZ, 'MMM d')}
            <span className="hidden sm:inline"> · {t('adminAnalytics.timezone', 'Asia/Beirut')}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          aria-label={t('common.refresh', 'Refresh')}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-indigo-200 dark:border-indigo-800
            text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 active:bg-indigo-100
            dark:active:bg-indigo-900/40 disabled:opacity-50 text-sm font-medium"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">{t('common.refresh', 'Refresh')}</span>
        </button>
      </div>

      {catchingUp && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 dark:border-amber-800
          bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 text-xs sm:text-sm text-amber-800 dark:text-amber-200">
          <ClockIcon className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            {t('adminAnalytics.catchingUp', 'Ingest is still catching up last month and this month. Rankings fill in as the 5-minute job progresses.')}
            {ingest?.lastTs ? (
              <span className="block mt-0.5 opacity-80">
                {t('adminAnalytics.ingestedThrough', 'Ingested through')}{' '}
                {formatInTimeZone(new Date(ingest.lastTs), TZ, 'MMM d, HH:mm')}
              </span>
            ) : null}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20
          px-3 py-2.5 text-xs sm:text-sm text-rose-700 dark:text-rose-200">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="p-3 sm:p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-3">
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <div>
            <label className="block text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              {t('dateRange.selectDateRange', 'Date Range')}
            </label>
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              className="w-full px-2.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600
                bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            >
              <option value="today">{t('dateRange.today', 'Today')}</option>
              <option value="last7">{t('dateRange.last7Days', 'Last 7 Days')}</option>
              <option value="thisMonth">{t('dateRange.thisMonth', 'This Month')}</option>
              <option value="lastMonth">{t('dateRange.lastMonth', 'Last Month')}</option>
              <option value="custom">{t('dateRange.custom', 'Custom')}</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              {t('adminAnalytics.currency', 'Currency')}
            </label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full px-2.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600
                bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            >
              <option value="all">{t('adminAnalytics.allCurrencies', 'All Currencies')}</option>
              {CURRENCIES.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>
        </div>

        {preset === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            <div>
              <label className="block text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                {t('dateRange.startDateTime', 'Start')}
              </label>
              <input
                type="datetime-local"
                value={toDateTimeLocal(customStart || dateRange.start)}
                onChange={(e) => setCustomStart(fromZonedTime(e.target.value, TZ))}
                className="w-full px-2.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600
                  bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                {t('dateRange.endDateTime', 'End')}
              </label>
              <input
                type="datetime-local"
                value={toDateTimeLocal(customEnd || dateRange.end)}
                onChange={(e) => setCustomEnd(fromZonedTime(e.target.value, TZ))}
                className="w-full px-2.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600
                  bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div ref={tabsRef} role="tablist" className="scroll-mt-4 grid grid-cols-3 gap-1 p-1 rounded-2xl bg-gray-100 dark:bg-gray-800/80">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={`py-2 rounded-xl text-xs sm:text-sm font-semibold transition-colors ${
              tab === item.id
                ? 'bg-white dark:bg-gray-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {loading && !data ? (
        <div className="py-16 flex flex-col items-center gap-3 text-gray-500 dark:text-gray-400">
          <ArrowPathIcon className="w-6 h-6 animate-spin" />
          <span className="text-sm">{t('common.loading', 'Loading...')}</span>
        </div>
      ) : (
        <>
          {tab === 'overview' && (
            <>
              <SummaryCard
                label={`${t('adminAnalytics.totalGGR', 'Total GGR')} · ${unitLabel}`}
                value={money(totalGgr, unit)}
                valueTitle={moneyTitle(totalGgr, unit)}
                tone={ggrTone(totalGgr)}
                parts={[
                  { label: t('adminAnalytics.casino', 'Casino'), value: money(casinoTotals.ggr, unit), title: moneyTitle(casinoTotals.ggr, unit), tone: ggrTone(casinoTotals.ggr) },
                  { label: t('adminAnalytics.sport', 'Sports'), value: money(sportTotals.ggr, unit), title: moneyTitle(sportTotals.ggr, unit), tone: ggrTone(sportTotals.ggr) }
                ]}
                stats={[
                  moneyStat(turnoverLabel, totalTurnover),
                  { label: holdLabel, value: holdPct(totalGgr, totalTurnover) },
                  { label: betsLabel, value: count(casinoTotals.bets + sportTotals.bets) }
                ]}
                note={convertedNote}
              />

              <div className="flex items-center justify-between gap-3 px-1">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('adminAnalytics.rankBy', 'Rank by')}</span>
                <Segmented options={metricOptions} value={metric} onChange={setMetric} label={t('adminAnalytics.rankBy', 'Rank by')} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-start">
                <section className="space-y-2">
                  <SectionHeader
                    title={t('adminAnalytics.topProvidersShort', 'Top providers')}
                    action={providers.length > OVERVIEW_ROWS ? (
                      <LinkButton onClick={() => goTo('casino')}>
                        {t('adminAnalytics.seeAll', { count: count(providers.length) })}
                      </LinkButton>
                    ) : null}
                  />
                  {rankedSection(rankedProviders, OVERVIEW_ROWS, providerRow, t('adminAnalytics.noProviders', 'No providers in this range'))}
                </section>

                <section className="space-y-2">
                  <SectionHeader
                    title={t('adminAnalytics.topGamesShort', 'Top games')}
                    action={games.length > OVERVIEW_ROWS ? (
                      <LinkButton onClick={() => goTo('casino')}>
                        {t('adminAnalytics.seeAll', { count: count(games.length) })}
                      </LinkButton>
                    ) : null}
                  />
                  {rankedSection(rankedGames, OVERVIEW_ROWS, gameRow, t('adminAnalytics.noGames', 'No casino games in this range'))}
                </section>
              </div>

              <section className="space-y-2">
                <SectionHeader
                  title={t('adminAnalytics.sportsBooks', 'Sports books')}
                  action={leagues.length > 0 ? (
                    <LinkButton onClick={() => goTo('sport')}>{t('adminAnalytics.seeLeagues', 'See leagues')}</LinkButton>
                  ) : null}
                />
                {rankedSection(rankedBooks, rankedBooks.length, bookRow, t('adminAnalytics.noSports', 'No sports activity in this range'), ggrOf)}
              </section>

              {converted && liveCurrencies.length > 0 && (
                <Disclosure
                  title={t('adminAnalytics.currencySummary', 'By Currency')}
                  hint={t('adminAnalytics.byCurrencyHint', 'Native amounts per currency')}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3">
                    {liveCurrencies.map(({ code, casino, sport }) => (
                      <div key={code} className="p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2.5">
                          <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{code}</span>
                          <Pill>{holdLabel} {holdPct(casino.ggr || 0, casino.betAmount || 0)}</Pill>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                          <Stat
                            label={`${t('adminAnalytics.casino', 'Casino')} ${ggrLabel}`}
                            value={money(casino.ggr || 0, code)}
                            title={moneyTitle(casino.ggr || 0, code)}
                            className={ggrTone(casino.ggr || 0)}
                          />
                          <Stat
                            label={`${t('adminAnalytics.sport', 'Sports')} ${ggrLabel}`}
                            value={money(sport.ggr || 0, code)}
                            title={moneyTitle(sport.ggr || 0, code)}
                            className={ggrTone(sport.ggr || 0)}
                          />
                          <Stat
                            label={turnoverLabel}
                            value={money(casino.betAmount || 0, code)}
                            title={moneyTitle(casino.betAmount || 0, code)}
                          />
                          <Stat label={playersLabel} value={count(sport.uniquePlayers || 0)} />
                        </div>
                      </div>
                    ))}
                  </div>
                </Disclosure>
              )}
            </>
          )}

          {tab === 'casino' && (
            <>
              <SummaryCard
                label={`${t('adminAnalytics.casino', 'Casino')} ${ggrLabel} · ${unitLabel}`}
                value={money(casinoTotals.ggr, unit)}
                valueTitle={moneyTitle(casinoTotals.ggr, unit)}
                tone={ggrTone(casinoTotals.ggr)}
                parts={[
                  { label: t('adminAnalytics.providersCount', { count: count(providers.length) }) },
                  { label: t('adminAnalytics.gamesCount', { count: count(games.length) }) }
                ]}
                stats={[
                  moneyStat(turnoverLabel, casinoTotals.turnover),
                  { label: holdLabel, value: holdPct(casinoTotals.ggr, casinoTotals.turnover) },
                  { label: betsLabel, value: count(casinoTotals.bets) }
                ]}
                note={convertedNote}
              />

              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('adminAnalytics.searchGames', 'Search games or providers')}
                    className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600
                      bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <Segmented options={metricOptions} value={metric} onChange={setMetric} label={t('adminAnalytics.rankBy', 'Rank by')} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-start">
                <section className="space-y-2">
                  <SectionHeader
                    title={t('adminAnalytics.allProviders', 'Providers')}
                    action={<Pill>{count(rankedProviders.length)}</Pill>}
                  />
                  {rankedSection(rankedProviders, providerLimit, providerRow, t('adminAnalytics.noProviders', 'No providers in this range'))}
                  <LoadMore
                    remaining={rankedProviders.length - providerLimit}
                    onClick={() => setProviderLimit((n) => n + PAGE_SIZE)}
                    label={t('common.loadMore', 'Load More')}
                  />
                </section>

                <section className="space-y-2">
                  <SectionHeader
                    title={t('adminAnalytics.allGames', 'Games')}
                    action={<Pill>{count(rankedGames.length)}</Pill>}
                  />
                  {rankedSection(rankedGames, gameLimit, gameRow, t('adminAnalytics.noGames', 'No casino games in this range'))}
                  <LoadMore
                    remaining={rankedGames.length - gameLimit}
                    onClick={() => setGameLimit((n) => n + PAGE_SIZE)}
                    label={t('common.loadMore', 'Load More')}
                  />
                </section>
              </div>
            </>
          )}

          {tab === 'sport' && (
            <>
              <SummaryCard
                label={`${t('adminAnalytics.sport', 'Sports')} ${ggrLabel} · ${unitLabel}`}
                value={money(sportTotals.ggr, unit)}
                valueTitle={moneyTitle(sportTotals.ggr, unit)}
                tone={ggrTone(sportTotals.ggr)}
                parts={rankedBooks.map((book) => ({
                  label: book.name,
                  value: money(book.ggr, unit),
                  title: moneyTitle(book.ggr, unit),
                  tone: ggrTone(book.ggr)
                }))}
                stats={[
                  moneyStat(turnoverLabel, sportTotals.turnover),
                  { label: playersLabel, value: count(sportTotals.players) },
                  { label: betsLabel, value: count(sportTotals.bets) }
                ]}
                note={convertedNote}
              />

              <section className="space-y-2">
                <SectionHeader title={t('adminAnalytics.sportsBooks', 'Sports books')} />
                {rankedSection(rankedBooks, rankedBooks.length, bookRow, t('adminAnalytics.noSports', 'No sports activity in this range'), ggrOf)}
              </section>

              {liveCurrencies.length > 0 && (data?.sports?.books || []).length > 0 && (
                <Disclosure title={t('adminAnalytics.playersByCurrency', 'Unique players by currency')}>
                  <Chart type="bar" height="240px" data={playersChart} options={groupedOptions} />
                </Disclosure>
              )}

              <section className="space-y-2">
                <SectionHeader
                  title={t('adminAnalytics.leagues', 'Leagues by GGR')}
                  action={(
                    <Segmented
                      options={[
                        { id: 'winning', label: t('adminAnalytics.winning', 'Winning') },
                        { id: 'losing', label: t('adminAnalytics.losing', 'Losing') }
                      ]}
                      value={leagueView}
                      onChange={setLeagueView}
                      label={t('adminAnalytics.leagues', 'Leagues by GGR')}
                    />
                  )}
                />
                {rankedSection(rankedLeagues, leagueLimit, leagueRow, t('adminAnalytics.noLeagues', 'No league data in this range yet'), ggrOf)}
                <LoadMore
                  remaining={rankedLeagues.length - leagueLimit}
                  onClick={() => setLeagueLimit((n) => n + PAGE_SIZE)}
                  label={t('common.loadMore', 'Load More')}
                />
                <p className="px-1 text-[11px] text-gray-500 dark:text-gray-400">
                  {t('adminAnalytics.leagueNote', 'Multi-leg slips are split across their leagues in proportion to selections, so league GGR adds up to the book total.')}
                </p>
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AdminAnalytics;
